import type { Virtualizer } from '@tanstack/react-virtual';

import { useSwimlaneRootProps } from '../../context';
import { GRID_GAP } from '../constants';
import { SwimlaneGridCell } from './SwimlaneGridCell';

interface ISwimlaneGrid {
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
}

export function SwimlaneGrid({ columnVirtualizer, rowVirtualizer }: ISwimlaneGrid) {
  const { rowHeaders, columnHeaders, showGridLines } = useSwimlaneRootProps();
  const rowVirtualItems = rowVirtualizer.getVirtualItems();
  const columnVirtualItems = columnVirtualizer.getVirtualItems();

  // Build cumulative offset map: sub-row index -> total px consumed by preceding group headers.
  // Mirrors the same logic in RowHeaders so cells align with their row headers.
  const subRowStartOffset = new Map<number, number>();
  const hasGroupHeaders = rowHeaders.some((h) => 'isGroupHeader' in h && h.isGroupHeader === true);
  let totalGroupHeaderOffset = 0;
  if (hasGroupHeaders) {
    let cumulativeOffset = 0;
    for (let i = 0; i < rowHeaders.length; i++) {
      const h = rowHeaders[i];
      if (h && 'isGroupHeader' in h && (h as { isGroupHeader?: boolean }).isGroupHeader === true) {
        cumulativeOffset += rowVirtualizer.options.estimateSize(i) + GRID_GAP.value;
      } else {
        subRowStartOffset.set(i, cumulativeOffset);
      }
    }
    totalGroupHeaderOffset = cumulativeOffset;
  }

  const gridHeight = rowVirtualizer.getTotalSize() - totalGroupHeaderOffset;

  return (
    <div
      style={{
        height: `${gridHeight}px`,
        width: `${columnVirtualizer.getTotalSize()}px`,
        gridArea: 'grid',
        position: 'relative',
        // Create a stacking context so row divs' z-indexes (which scale with the rendered
        // row count) stay below the sticky column headers.
        isolation: 'isolate',
      }}
    >
      {/* Full-height vertical column dividers. Rendered at the grid level so they span
          uninterrupted across rows that don't render cells (group headers, empty rows
          from hidden headers). Per-cell :before pseudo-elements on SwimlaneGridCell would
          otherwise leave visible gaps in those rows (NEX-11268). */}
      {showGridLines &&
        columnVirtualItems.map((virtualColumn) => {
          if (virtualColumn.index >= columnHeaders.length - 1) return null;
          return (
            <div
              key={`col-divider-${virtualColumn.key}`}
              aria-hidden="true"
              className="absolute top-0 bottom-0 w-px bg-border-neutral-bold/12 pointer-events-none"
              style={{
                left: `${virtualColumn.start + virtualColumn.size + GRID_GAP.value / 2}px`,
                zIndex: 0,
              }}
            />
          );
        })}
      {rowVirtualItems.map((virtualRow, index) => {
        const rowHeader = rowHeaders[virtualRow.index];
        const isGroupHeader =
          rowHeader && 'isGroupHeader' in rowHeader && rowHeader.isGroupHeader === true;
        const adjustedStart = virtualRow.start - (subRowStartOffset.get(virtualRow.index) ?? 0);
        const adjustedVirtualRow = subRowStartOffset.has(virtualRow.index)
          ? { ...virtualRow, start: adjustedStart }
          : virtualRow;
        return (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            ref={rowVirtualizer.measureElement}
            // assign progressively lower z-index to ensure that the higher rows are rendered on top
            // this is important for visibility of InlineAdd button
            style={{ position: 'relative', zIndex: rowVirtualItems.length - index }}
          >
            {!isGroupHeader &&
              columnVirtualItems.map((virtualColumn) => (
                <SwimlaneGridCell
                  key={`${virtualRow.key}-${virtualColumn.key}`}
                  virtualColumn={virtualColumn}
                  virtualRow={adjustedVirtualRow}
                />
              ))}
          </div>
        );
      })}
    </div>
  );
}
